/**
 * File helper utilities for consistent file URL handling across the application.
 * 
 * SINGLE SOURCE OF TRUTH:
 * - files.mockup.url - Primary mockup URL (set by FilesModal)
 * - designFileUrl - Backward compatible fallback
 * - mockupUrl - Legacy field (rarely used)
 */

/** Field names that hold mockup/artwork URLs. When merging or updating line data, preserve these if the update doesn't explicitly set them. */
export const ARTWORK_FIELDS = ['designFileUrl', 'artworkUrl', 'mockupUrl', 'files'];

/**
 * Returns true if the URL is safe to use for display or persistence. Blob URLs are
 * temporary and break after refresh or in other tabs, so we never use them for display
 * or save them to Firestore.
 */
function isDisplayableUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('blob:')) return false;
  return true;
}

/**
 * Use when writing mockup URL to Firestore. Returns the URL only if it is persistable
 * (not a blob). Returns null for blob or invalid URLs so we never save broken links.
 * @param {string|null} url - The mockup URL (e.g. from getMockupUrl(line))
 * @returns {string|null} - The URL to save, or null
 */
export function getPersistableMockupUrl(url) {
  return isDisplayableUrl(url) ? url : null;
}

/**
 * Get the mockup URL from a line item, checking all possible sources.
 * Use this everywhere you need to display a mockup image.
 * Never returns blob URLs (they are temporary and cause broken images on Lines page etc.).
 *
 * @param {Object} line - The line item object
 * @returns {string|null} - The mockup URL or null if none exists (or only blob URL exists)
 */
export function getMockupUrl(line) {
  if (!line) return null;

  const raw =
    line.files?.mockup?.url ||
    line.designFileUrl ||
    line.mockupUrl ||
    line.artworkUrl ||
    null;

  return isDisplayableUrl(raw) ? raw : null;
}

/**
 * Check if a line has any mockup image
 * 
 * @param {Object} line - The line item object
 * @returns {boolean} - True if line has a mockup
 */
export function hasMockup(line) {
  return !!getMockupUrl(line);
}

/**
 * Get production files from a line item
 * 
 * @param {Object} line - The line item object
 * @returns {Array} - Array of production files
 */
export function getProductionFiles(line) {
  if (!line) return [];
  return line.files?.production || [];
}

/**
 * Normalize file data for saving to Firestore.
 * Removes File objects and ensures consistent structure.
 * 
 * @param {Object} files - The files object from lineData
 * @returns {Object|null} - Cleaned files object for Firestore
 */
export function normalizeFilesForFirestore(files) {
  if (!files) return null;
  
  const normalized = {};
  
  // Handle mockup
  if (files.mockup) {
    const mockupUrl = files.mockup.url || null;
    // Don't save blob URLs - they're temporary and won't work later
    if (mockupUrl && mockupUrl.startsWith('blob:')) {
      console.warn('⚠️ Skipping blob URL in normalizeFilesForFirestore - file should be uploaded first');
      // Don't include blob URLs
    } else {
      normalized.mockup = {
        url: mockupUrl,
        fileName: files.mockup.fileName || null,
        // Don't include File objects - Firestore can't store them
      };
      // Only include if we have a valid URL
      if (!normalized.mockup.url) {
        delete normalized.mockup;
      }
    }
  }
  
  // Handle production files
  if (files.production && Array.isArray(files.production)) {
    normalized.production = files.production
      .map(file => {
        const fileUrl = file.url || null;
        // Don't save blob URLs - they're temporary and won't work later
        if (fileUrl && fileUrl.startsWith('blob:')) {
          console.warn('⚠️ Skipping production file with blob URL:', file.name);
          return null; // Filter this out
        }
        return {
          name: file.name,
          url: fileUrl,
          type: file.type || 'unassigned',
          // Don't include File objects or refs
        };
      })
      .filter(file => file !== null && file.url); // Only include files with valid URLs
  }
  
  return Object.keys(normalized).length > 0 ? normalized : null;
}

/**
 * Create the standard files structure for a line item
 * 
 * @param {string} mockupUrl - URL to the mockup image
 * @param {string} fileName - Original filename
 * @param {Array} productionFiles - Array of production file objects
 * @returns {Object} - Standard files structure
 */
export function createFilesStructure(mockupUrl, fileName = null, productionFiles = []) {
  const files = {};
  
  if (mockupUrl) {
    files.mockup = {
      url: mockupUrl,
      fileName: fileName
    };
  }
  
  if (productionFiles && productionFiles.length > 0) {
    files.production = productionFiles;
  }
  
  return Object.keys(files).length > 0 ? files : null;
}

